import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { toast } from 'sonner';
import { isBadRequestError } from '@/core/api/badRequestError';
import { removeExecutorService } from '@/entities/Order/api/removeExecutorService';
import { useOrders } from '@/entities/Order/model/store';
import {
    REMOVE_EXECUTOR_DEFAULTS,
    removeExecutorSchema,
    type RemoveExecutorFormValues,
} from '@/entities/Order/model/removeExecutorSchema';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { getErrorMessage } from '@/shared/lib/errors';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import classes from './RemoveExecutorModal.module.scss';

export interface RemoveExecutorModalProps {
    visible: boolean;
    onHide: () => void;
    orderId: string;
}

const { TextField, ToggleSwitchField } = createRHFFields<RemoveExecutorFormValues>();

/** Показывает оператору снятие исполнителя с комментарием и штрафом */
export const RemoveExecutorModal = ({ visible, onHide, orderId }: RemoveExecutorModalProps) => {
    const [completed, setCompleted] = useState(false);
    const getOrderById = useOrders((state) => state.getOrderById);

    const form = useUnsavedForm<RemoveExecutorFormValues>({
        resolver: zodResolver(removeExecutorSchema),
        defaultValues: REMOVE_EXECUTOR_DEFAULTS,
    }, visible);

    const { reset } = form;
    const isSubmitting = form.formState.isSubmitting;

    useEffect(() => {
        if (!visible) return;

        reset(REMOVE_EXECUTOR_DEFAULTS);
        setCompleted(false);
    }, [visible, orderId, reset]);

    /** Закрывает окно и обновляет заказ после операции */
    const handleHide = () => confirmUnsavedChanges(form.formState.isDirty, () => {
        reset(REMOVE_EXECUTOR_DEFAULTS);

        if (completed) void getOrderById(orderId);
        onHide();
    });

    /** Отправляет снятие исполнителя */
    const onSubmit = async (values: RemoveExecutorFormValues) => {
        form.clearErrors('root.server');

        try {
            await removeExecutorService.remove({
                orderId,
                comment: values.comment,
                hasPenalty: values.hasPenalty,
            });

            reset(values);
            setCompleted(true);
            toast.success('Исполнитель снят с заказа');
        } catch (error: unknown) {
            if (isBadRequestError(error)) return;

            form.setError('root.server', {
                type: 'server',
                message: getErrorMessage(error, 'Не удалось снять исполнителя'),
            });
        }
    };

    return <Modal
        visible={visible}
        onHide={handleHide}
        title="Снять исполнителя с заказа"
        size="lg"
        className={classes.modal}
        closable={!isSubmitting}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label={completed
                    ? 'Закрыть'
                    : 'Отмена'
                }
                text
                onClick={handleHide}
                disabled={isSubmitting}
            />
            {!completed &&
                <Button
                    type="submit"
                    form="remove-order-executor-form"
                    label="Снять исполнителя"
                    icon="pi pi-user-minus"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                />
            }
        </div>}
    >
        {completed
            ? <Message
                severity="success"
                text="Заказ снова в поиске исполнителя"
            />
            : <FormProvider {...form}>
                <form
                    id="remove-order-executor-form"
                    className={classes.form}
                    onSubmit={form.handleSubmit(onSubmit)}
                    noValidate
                >
                    <TextField
                        name="comment"
                        label="Комментарий оператора"
                        placeholder="Укажите причину снятия"
                        isRequired
                        disabled={isSubmitting}
                    />
                    <ToggleSwitchField
                        name="hasPenalty"
                        label="Назначить штраф исполнителю"
                        disabled={isSubmitting}
                    />
                    {isSubmitting &&
                        <RequestLoading message="Снимаем исполнителя..." />
                    }
                    {form.formState.errors.root?.server?.message &&
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    }
                </form>
            </FormProvider>
        }
    </Modal>;
};
